import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * Pílula compacta de aviso com uma ação opcional: ícone, rótulo e um botão circular à direita
 * (M2-F3/M2-D63 — o chamado de garçom na lista e no mapa da tela de contas).
 *
 * **Um componente só para as duas telas, e isso é o ponto** (validação em tela de 2026-09-22): quando a lista
 * desenhava a pílula dentro do template do `hmax-table`, a regra `.icon-size-4 { margin-left: 4px !important }`
 * do SCSS da tabela empurrava o sino e o check para a direita, e a pílula da lista saía diferente da do mapa
 * com as mesmas classes. Aqui o template é encapsulado: estilo de quem hospeda não alcança os ícones.
 *
 * **Cores:** por input (valor CSS pronto, como o `InlineBadge` do `hmax-table`) ou herdadas das classes do
 * host — `class="bg-card text-amber-800 dark:text-amber-200"` —, que é o que deixa o mapa ter variante de tema
 * escuro. O ícone segue `currentColor`; o botão é sempre um disco com ícone branco.
 *
 * **A ação para a propagação** antes de emitir: a pílula mora dentro de linhas e quadrados clicáveis, e dar
 * baixa num aviso não pode abrir o detalhe junto.
 */
@Component({
  selector: 'hmax-action-pill',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatTooltipModule],
  host: {
    class: 'inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full py-1 text-xs font-semibold leading-4',
    // 🔴 Classe computada, e NÃO `[class.pl-1.5]`: o Angular corta o nome da binding no ponto e aplica `pl-1`
    // (o `.5` vira "unidade"). Foi assim que a pílula da lista saiu com o respiro errado.
    '[class]': 'paddingClass()',
    '[style.background-color]': 'background()',
    '[style.color]': 'color()',
  },
  template: `
    <span class="inline-flex items-center gap-1.5" [matTooltip]="tooltip() ?? ''" [matTooltipDisabled]="!tooltip()">
      @if (icon(); as iconName) {
        <mat-icon class="icon-size-4 shrink-0 text-current!" [svgIcon]="iconName"></mat-icon>
      }
      <span>{{ label() }}</span>
    </span>
    @if (actionIcon(); as actionIconName) {
      <button
        type="button"
        class="pointer-events-auto flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full
          hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        [style.background-color]="actionColor()"
        [disabled]="actionDisabled()"
        [attr.aria-label]="actionLabel()"
        [matTooltip]="actionLabel() ?? ''"
        [matTooltipDisabled]="!actionLabel()"
        [attr.data-testid]="actionTestId()"
        (click)="press($event)"
      >
        <mat-icon class="icon-size-4 text-white!" [svgIcon]="actionIconName"></mat-icon>
      </button>
    }
  `,
})
export class ActionPillComponent {
  readonly label = input.required<string>();
  /** `svgIcon` à esquerda do rótulo. */
  readonly icon = input<string | undefined>(undefined);
  /** Tooltip do ícone + rótulo — alvo separado do da ação, senão os dois tooltips abririam juntos. */
  readonly tooltip = input<string | undefined>(undefined);
  /** Fundo da pílula (valor CSS). Ausente, vale a classe do host. */
  readonly background = input<string | undefined>(undefined);
  /** Cor do texto e do ícone (valor CSS). Ausente, vale a classe do host. */
  readonly color = input<string | undefined>(undefined);

  /** `svgIcon` da ação. Sem ele, a pílula não tem botão. */
  readonly actionIcon = input<string | undefined>(undefined);
  /** Já traduzido: vira tooltip e `aria-label` do botão. */
  readonly actionLabel = input<string | undefined>(undefined);
  /** Fundo do disco da ação (valor CSS). */
  readonly actionColor = input<string | undefined>(undefined);
  readonly actionDisabled = input(false);
  readonly actionTestId = input<string | undefined>(undefined);

  readonly actionPressed = output<void>();

  /** Com ação, o respiro é o mesmo dos dois lados do disco; sem ela, o texto respira igual dos dois lados. */
  protected readonly paddingClass = computed(() => (this.actionIcon() ? 'pl-1.5 pr-1' : 'px-2.5'));

  protected press(event: Event): void {
    event.stopPropagation();
    this.actionPressed.emit();
  }
}
